import { useState } from 'react'
import { CircleDashedIcon, CircleCheckIcon, FileText, KeyRound, Send } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import DocUrlStep from './diy/DocUrlStep'
import CredentialStep from './diy/CredentialStep'
import SendMessageStep from './diy/SendMessageStep'
import type { DiyWorker } from '@/frontend/pages/Dashboard/Integrations/types'

interface Props {
  workers: DiyWorker[]
  initialStep?: number
  initialDocUrl?: string
  initialCredentialName?: string
  initialCredentialId?: string
}

const STEPS = [
  { key: 'doc', label: 'Documentação da API', icon: FileText },
  { key: 'credential', label: 'Criar credencial', icon: KeyRound },
  { key: 'send', label: 'Enviar para o agente', icon: Send },
]

export default function DiyView({ workers, initialStep = 1, initialDocUrl = '', initialCredentialName = '', initialCredentialId = '' }: Props) {
  const [active, setActive] = useState(`step-${initialStep}`)
  const [completed, setCompleted] = useState<Set<number>>(() => {
    const set = new Set<number>()
    for (let i = 1; i < initialStep; i++) set.add(i)
    return set
  })
  const [docUrl, setDocUrl] = useState(initialDocUrl)
  const [credentialName] = useState(initialCredentialName)
  const [credentialId] = useState(initialCredentialId)
  const [workerName, setWorkerName] = useState('')

  const completeStep = (step: number) => {
    setCompleted((prev) => new Set(prev).add(step))
    if (step < STEPS.length) setActive(`step-${step + 1}`)
  }

  const summaries: Record<number, string | undefined> = {
    1: docUrl || undefined,
    2: credentialName || undefined,
    3: workerName || undefined,
  }

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardContent className="p-6">
          <Accordion type="single" collapsible value={active} onValueChange={setActive} className="space-y-2">
            {STEPS.map((step, index) => {
              const stepNum = index + 1
              const isCompleted = completed.has(stepNum)
              const Icon = step.icon
              const summary = summaries[stepNum]

              return (
                <AccordionItem key={step.key} value={`step-${stepNum}`} className="rounded-md !border px-4">
                  <AccordionTrigger className="py-3">
                    <span className="flex items-center gap-3">
                      {isCompleted ? (
                        <CircleCheckIcon className="h-5 w-5 shrink-0 text-success" />
                      ) : (
                        <CircleDashedIcon className="h-5 w-5 shrink-0 text-muted-foreground" />
                      )}
                      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="flex flex-col items-start">
                        <span>{step.label}</span>
                        {isCompleted && summary && (
                          <span className="text-xs text-muted-foreground font-normal truncate max-w-[300px]">{summary}</span>
                        )}
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-1 pb-2">
                    {stepNum === 1 && (
                      <DocUrlStep docUrl={docUrl} onChange={setDocUrl} onNext={() => completeStep(1)} />
                    )}
                    {stepNum === 2 && (
                      <CredentialStep docUrl={docUrl} />
                    )}
                    {stepNum === 3 && (
                      <SendMessageStep
                        workers={workers}
                        credentialId={credentialId}
                        credentialName={credentialName}
                        docUrl={docUrl}
                        onComplete={(name) => { setWorkerName(name); completeStep(3) }}
                      />
                    )}
                  </AccordionContent>
                </AccordionItem>
              )
            })}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  )
}
